import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import {
  selectContactSegments,
  selectContactSegmentsLoading,
  selectContactSegmentsError,
  selectContactSegmentsPagination,
} from 'store/contacts/contacts.selector';
import { getContactSegmentsRequest } from 'store/contacts/contacts.action';
import { Link } from 'react-router-dom';

const formatDate = (date: string | Date) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateObj);
};

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface SegmentsTabProps {
  contactId: string;
}

const SegmentsTab: React.FC<SegmentsTabProps> = ({ contactId }) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All Types');

  // Debounce search term with 500ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to first page when search term or type changes
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, selectedType]);

  // Redux selectors
  const contactSegments = useSelector(selectContactSegments);
  const loading = useSelector(selectContactSegmentsLoading);
  const error = useSelector(selectContactSegmentsError);
  const pagination = useSelector(selectContactSegmentsPagination);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized && pagination) {
      const hasPaginationData =
        pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0;

      if (hasPaginationData) {
        if (currentPage !== pagination.page || pageSize !== pagination.limit) {
          setCurrentPage(pagination.page);
          setPageSize(pagination.limit);
        }
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized, currentPage, pageSize]);

  // Fetch contact segments when component mounts or contactId changes
  useEffect(() => {
    if (isInitialized && contactId) {
      dispatch(
        getContactSegmentsRequest(contactId, {
          page: currentPage,
          limit: pageSize,
          search: debouncedSearchTerm || undefined,
          type: selectedType !== 'All Types' ? selectedType : undefined,
        })
      );
    }
  }, [
    contactId,
    currentPage,
    pageSize,
    debouncedSearchTerm,
    selectedType,
    dispatch,
    isInitialized,
  ]);

  // Use segments directly from API (server-side filtering)
  const segments = contactSegments;

  // Calculate pagination display
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, segments.length);
  const hasNextPage = (currentPage + 1) * pageSize < segments.length;
  const hasPrevPage = currentPage > 0;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page
  };

  const handleTypeFilter = (type: string) => {
    setSelectedType(type);
    setCurrentPage(0); // Reset to first page
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Segments</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading segments...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Segments</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-destructive">
            Error loading segments: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Segments</h3>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <input
            placeholder="Find by name or description"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-60 px-3 py-2 border border-border rounded-lg bg-card text-card-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
          <div className="relative">
            <select
              value={selectedType}
              onChange={(e) => handleTypeFilter(e.target.value)}
              className="appearance-none bg-card border border-border rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
            >
              <option>All Types</option>
              <option>Smart</option>
              <option>Manual</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <div className="relative">
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="appearance-none bg-card border border-border rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[200px]">
                Name
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                Contacts
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                Usage
              </th>
            </tr>
          </thead>
          <tbody>
            {segments.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No segments found
                </td>
              </tr>
            ) : (
              segments.map((segment) => (
                <tr
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  key={segment._id}
                >
                  <td className="px-4 py-3 whitespace-nowrap min-w-[200px]">
                    <div className="flex flex-col">
                      <Link
                        to={
                          segment.type === 'Manual'
                            ? `/segments/manual/${segment._id}`
                            : `/segments/smart/${segment._id}`
                        }
                        className="flex items-center gap-1"
                      >
                        <span className="text-sm text-primary font-medium cursor-pointer hover:underline">
                          {segment.name}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground ml-1" />
                      </Link>
                      <div className="text-xs text-muted-foreground mt-1">
                        Created at {formatDate(segment.createdAt!)} | Last
                        edited {formatDate(segment.updatedAt!)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        segment.type === 'Smart'
                          ? 'bg-primary-background text-primary'
                          : 'bg-success-background text-success'
                      }`}
                    >
                      {segment.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-primary font-semibold">
                      {segment.contacts?.length || 0} people{' '}
                      <ChevronRight className="w-4 h-4 ml-1 inline" />
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-muted-foreground flex gap-2 items-center">
                      <span className="text-xs">Unused</span>
                      {Array.from({ length: 2 }).map((_, idx) => (
                        <Eye
                          key={idx}
                          className="w-4 h-4 text-muted-foreground inline -ml-1"
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {segments.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {startItem}-{endItem} of {segments.length} segments
          </div>

          <div className="flex items-center space-x-2">
            <button
              className={`p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                !hasPrevPage ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={!hasPrevPage}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-sm text-gray-500 px-3 py-1">
              Page {currentPage + 1}
            </span>

            <button
              className={`p-2 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                !hasNextPage ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={!hasNextPage}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentsTab;
